/**
    content-editor - only displayed when an admin is logged in

    props:
        updateUrl: String, url to pass update data to
        action: String, passed to back end to define different requests
        instanceId: String, used to differentiate multiple editors on the same window
        displayId: String, ID of the element shown to the user with the content inside
        editorPlaceholder: String, some optional placeholder text for when the text box is empty
        editorInfo: String, some additional info shown above the editor box
        allowEmbeds: Boolean, allow embedding images/video urls
        startHeight: String (as consumed by TinyMCE), starting height of the editor box
*/

<!--suppress JSUnresolvedVariable, JSUnusedGlobalSymbols -->
<template>
    <div class="editor-container">
        <div class="edit-block" v-on:click.prevent="initialiseEditor">
            <font-awesome-icon :icon="['far', 'edit']" size="lg"></font-awesome-icon>
        </div>

        <div class="editor-placeholder" v-if="!existingContent && editorPlaceholder">{{ editorPlaceholder }}</div>

        <div class="editor-info" v-if="this.editorInfo && this.showEditor">{{ this.editorInfo }}</div>

        <div class="editor-main" v-if="this.showEditor">
            <textarea v-bind:id="instanceId"></textarea>
        </div>

        <div class="editor-errors" v-if="errorMessages">{{ errorMessages }}</div>
    </div>
</template>


<script>
import {formatXHRError} from "../../js/general-tools.js";

import tinymce from "tinymce";
import "tinymce/icons/default";
import "tinymce/themes/silver";
import "tinymce/skins/ui/oxide/skin.css"
import "tinymce/plugins/lists";
import "tinymce/plugins/table";
import "tinymce/plugins/image";
import "tinymce/plugins/imagetools";
import "tinymce/plugins/media";

import { library } from "@fortawesome/fontawesome-svg-core";
import { faEdit, faSave, faWindowClose } from "@fortawesome/free-regular-svg-icons";
library.add(faEdit, faSave, faWindowClose);


export default {
    name: "content-editor",
    components: {
    },
    props: {
        updateUrl: {required: true, type: String},
        action: {required: true, type: String},
        instanceId: {required: true, type: String},
        displayId: {required: true, type: String},
        editorPlaceholder: {required: false, type: String},
        editorInfo: {required: false, type: String},
        allowEmbeds: {type: Boolean, default: false},
        startHeight: {required: true, type: String}
    },
    data: function () {
        return {
            editorRef: null,
            existingContent: "",
            showEditor: false,
            errorMessages: "",
            savingChanges: false
        }
    },
    methods: {
        initialiseEditor: function () {
            if (this.showEditor) {
                return;
            }

            this.showEditor = true;
            const thisCopy = this;

            this.$nextTick(function () {
                this.existingContent = document.getElementById(this.displayId).innerHTML;

                let toolbarOptions = "saveButton cancelButton | formatselect |  bold italic strikethrough superscript subscript | alignleft aligncenter alignright outdent indent | numlist bullist table | link";
                if (this.allowEmbeds) {
                    toolbarOptions += " | image media"
                }

                this.editorRef = tinymce.init({
                    selector: "#" + this.instanceId,
                    imagetools_cors_hosts: ['mcsuk.org', 'www.mcsuk.org', 'mpa.local.mpa-reality-check.org'],
                    ally_advanced_options: true,
                    file_picker_types: "image",
                    plugins: "image imagetools media lists table link",
                    menubar: "",
                    toolbar: toolbarOptions,
                    height: thisCopy.startHeight,
                    images_upload_handler: function (blobInfo, success, failure) {
                        // https://github.com/tinymce/tinymce/issues/3942
                        let xhr = new XMLHttpRequest();
                        xhr.open('POST', '/api/1/ul/');
                        xhr.setRequestHeader('X-CSRFToken', mparc.csrf); // manually set header

                        xhr.onload = function() {
                            if (xhr.status !== 200) {
                                failure('HTTP Error: ' + xhr.status);
                                return;
                            }

                            let json = JSON.parse(xhr.responseText);

                            if (!json || typeof json.location !== 'string') {
                                failure('Invalid JSON: ' + xhr.responseText);
                                return;
                            }

                            success(json.location);
                        };

                        let formData = new FormData();
                        formData.append('file', blobInfo.blob(), blobInfo.filename());

                        xhr.send(formData);
                    },
                    setup: function(editor) {
                        editor.on("init", function() {
                            tinymce.activeEditor.setContent(thisCopy.existingContent)
                        });

                        editor.ui.registry.addButton("cancelButton", {
                            text: "<span class='cancel-button'>Cancel</span>",
                            onAction: function () {
                                if (editor.getContent() !== thisCopy.existingContent) {
                                    if (!window.confirm("Are you sure you want to clear your changes?")) {
                                        return
                                    }
                                }
                                editor.remove();
                                thisCopy.editorRef = null;
                                thisCopy.showEditor = false;
                            }
                        });

                        editor.ui.registry.addButton("saveButton", {
                            text: "<span class='save-button'>Save</span>",
                            onAction: function () {
                                thisCopy.errorMessages = "";
                                thisCopy.savingChanges = true;
                                console.log(axios)
                                console.log(axios.patch)
                                axios.patch(thisCopy.updateUrl, {
                                    action: thisCopy.action,
                                    generic_text: editor.getContent()

                                }).then(() => {
                                    document.getElementById(thisCopy.displayId).innerHTML = editor.getContent();
                                    editor.remove();
                                    thisCopy.editorRef = null;
                                    thisCopy.showEditor = false;

                                }).catch((error) => {
                                    thisCopy.errorMessages = formatXHRError(error, "PATCH")

                                }).then(() => {
                                    thisCopy.savingChanges = false;
                                })
                            }
                        });
                    }
                })

            })
        },
    },
    computed: {},
    watch: {},
    mounted: function () {
        // additionally set here to allow display of placeholder only if required.
        this.existingContent = document.getElementById(this.displayId).innerHTML;
    }
}
</script>


<style lang="scss">
    @import "../../styles/variables.scss";

    .editor-container {
        position: absolute;
        top: 0; left: 0; right: 0;
        z-index: 1001;
        background-color: white;
    }

    .editor-placeholder {
        font-style: italic;
    }

    .editor-main {
        //border: 1px solid green;
    }

    .editor-info {
        margin: 5px 5px 5px 15px;
        font-size: 80%;
    }

    .editor-errors {
        font-size: 90%;
        margin: 8px 0 5px 15px;
        color: red;
    }

    .save-button {
        color: darkgreen !important;
        font-weight: bold !important;
    }

    .cancel-button {
        color: darkred !important;
        font-weight: bold !important;
    }

</style>